import React, { ReactNode, useEffect, useRef, useState } from 'react'
import * as ReactBootstrap from 'react-bootstrap'
import Autosuggest from 'react-autosuggest'

import { RestService } from '../../services'

// Wait for typing to pause before hitting the backend.
const SEARCH_DEBOUNCE_MS = 350

interface AutoSuggestFieldProps {
  label: string
  // REST endpoint to search (e.g. 'organizations', 'users'). Hit with ?search=.
  endpoint: string
  getSuggestionLabel: (suggestion: any) => string
  // Called when a suggestion is picked.
  onSelect: (suggestion: any) => void
  // When provided, free-typed text is pushed to the parent too (free-text fields
  // like "Sold by"). Omit for selection-only fields like "Organization".
  onTextChange?: (text: string) => void
  initialText?: string
  placeholder?: string
  required?: boolean
  icon?: ReactNode
  helpText?: string
  // Optional rich rendering for a suggestion row. Falls back to the plain
  // label. The stored/typed value always comes from getSuggestionLabel.
  renderSuggestion?: (suggestion: any) => ReactNode
}

// Controlled autosuggest for the plain-React Support Package form. The app's
// existing EaseOnAutoSuggest is bound to redux-form, so it can't be reused here;
// this reuses the same server-side `?search=` convention via RestService.
export const AutoSuggestField: React.FC<AutoSuggestFieldProps> = ({
  label,
  endpoint,
  getSuggestionLabel,
  onSelect,
  onTextChange,
  initialText = '',
  placeholder,
  required,
  icon,
  helpText,
  renderSuggestion,
}) => {
  const [text, setText] = useState(initialText)
  const [suggestions, setSuggestions] = useState<any[]>([])

  // Debounce timer and a monotonically increasing request id so that only the
  // latest in-flight response is allowed to update state (drops stale results).
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const requestIdRef = useRef(0)

  // Re-seed when the edited record changes.
  useEffect(() => {
    setText(initialText)
  }, [initialText])

  // Cancel any pending debounce on unmount.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    },
    []
  )

  const fetchSuggestions = ({ value }: { value: string }) => {
    const query = value.trim()
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!query) {
      setSuggestions([])
      return
    }
    debounceRef.current = setTimeout(() => {
      const requestId = ++requestIdRef.current
      new RestService(endpoint)
        .makeSearchRequest(query)
        .then((data: any) => {
          // Ignore responses superseded by a newer keystroke.
          if (requestId !== requestIdRef.current) return
          setSuggestions(data?.results ?? [])
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return
          setSuggestions([])
        })
    }, SEARCH_DEBOUNCE_MS)
  }

  return (
    <ReactBootstrap.Form.Group className="mb-3">
      <ReactBootstrap.Form.Label className="fw-semibold small text-secondary mb-1">
        {icon ? <span className="me-2 text-muted">{icon}</span> : null}
        {label}
        {required ? <span className="text-danger ms-1">*</span> : null}
      </ReactBootstrap.Form.Label>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={fetchSuggestions}
        onSuggestionsClearRequested={() => setSuggestions([])}
        getSuggestionValue={getSuggestionLabel}
        renderSuggestion={(suggestion: any, { isHighlighted }: any) => (
          <div className={`px-3 py-2 ${isHighlighted ? 'bg-light' : ''}`}>
            {renderSuggestion
              ? renderSuggestion(suggestion)
              : getSuggestionLabel(suggestion)}
          </div>
        )}
        onSuggestionSelected={(_event: any, { suggestion }: any) =>
          onSelect(suggestion)
        }
        inputProps={{
          className: 'form-control',
          placeholder: placeholder ?? '',
          value: text,
          required: !!required,
          onChange: (_event: any, { newValue }: { newValue: string }) => {
            setText(newValue)
            onTextChange?.(newValue)
          },
        }}
      />
      {helpText ? (
        <ReactBootstrap.Form.Text className="text-muted">
          {helpText}
        </ReactBootstrap.Form.Text>
      ) : null}
    </ReactBootstrap.Form.Group>
  )
}
